import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import AppButton from "@/features/line-sheet-sets/app-button";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import SVG from "react-inlinesvg";
import { fileIconsByType } from "@/shared/constants";
import useDownloadUnoFile from "@/hooks/use-download-uno-file";
import ProductConditionPanel, {
  ProductConditionEntry,
} from "@/features/line-sheet-sets/product-condition-panel";
import {
  ParseOrderRulesToConditionsBrandCondition,
  formatMatchedConditionLabel,
} from "@/features/line-sheet-sets/hooks/use-parse-order-rules-to-conditions";

export type { ProductConditionEntry };

interface ApplyConditionProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  lineSheetSetId: number;
  target:
    | { kind: "BRAND"; id: number; name: string }
    | { kind: "LINE_SHEET"; id: number; name: string }
    | { kind: "SEASON"; id: number; name: string; seasonName: string }
    | {
        kind: "LINE_SHEET_BRAND";
        lineSheetId: number;
        brandId: number;
        name: string;
      }
    | {
        kind: "SEASON_BRAND";
        seasonName: string;
        brandId: number;
        name: string;
      };
  initialEntries?: Map<number, { by: "MARKUP" | "DISCOUNT"; value: string }>;
  matchedConditions?: ParseOrderRulesToConditionsBrandCondition[];
  orderRulesText?: string;
  orderRuleFiles?: { id: number; name: string }[];
  onConfirm: (entries: ProductConditionEntry[]) => void;
}

export default function ApplyConditionProductModal({
  isOpen,
  onClose,
  lineSheetSetId,
  target,
  initialEntries,
  matchedConditions,
  orderRulesText,
  orderRuleFiles,
  onConfirm,
}: ApplyConditionProductModalProps) {
  const { tTitle } = useI18nHelper();
  const orderRuleFileList = orderRuleFiles ?? [];
  const matchedConditionList = matchedConditions ?? [];
  const hasOrderRuleContent =
    (orderRulesText?.trim() ?? "") !== "" ||
    orderRuleFileList.length > 0 ||
    matchedConditionList.length > 0;
  const [isOrderRulePanelOpen, setIsOrderRulePanelOpen] = useState(true);
  const { fire: fireDownloadUnoFile } = useDownloadUnoFile();
  const [entries, setEntries] = useState<ProductConditionEntry[]>([]);

  const handleConfirm = () => {
    onConfirm(entries);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={"6xl"}
      scrollBehavior={"inside"}
    >
      <ModalOverlay />
      <ModalContent overflow={"hidden"}>
        <ModalHeader
          color={"#444440"}
          fontSize={"14px"}
          background={"#F7F9FA"}
          borderBottom={"0.5px solid #8F8F8C"}
        >
          {tTitle("apply_order_rule")} -{" "}
          {target.kind === "BRAND" ||
          target.kind === "LINE_SHEET_BRAND" ||
          target.kind === "SEASON_BRAND"
            ? `[${tTitle("brand")}]`
            : `[${tTitle("line_sheet")}]`}{" "}
          {target.name}
        </ModalHeader>
        <ModalBody>
          <Flex gap={"12px"} alignItems={"stretch"}>
            {hasOrderRuleContent && (
              <Box
                width={isOrderRulePanelOpen ? "240px" : "36px"}
                flexShrink={0}
                borderRadius={"6px"}
                border={"0.5px solid #D4D4D1"}
                background={"#FFF7CC"}
                transition={"width 0.15s"}
                overflow={"hidden"}
                alignSelf={"flex-start"}
              >
                <Flex
                  alignItems={"center"}
                  justifyContent={"space-between"}
                  padding={"8px 10px"}
                  cursor={"pointer"}
                  onClick={() => setIsOrderRulePanelOpen((v) => !v)}
                  borderBottom={
                    isOrderRulePanelOpen ? "0.5px solid #E5DCA0" : "none"
                  }
                >
                  {isOrderRulePanelOpen && (
                    <Text fontSize={"12px"} fontWeight={"700"}>
                      {tTitle("order_rule")}
                    </Text>
                  )}
                  <Box
                    transform={
                      isOrderRulePanelOpen ? "rotate(0deg)" : "rotate(180deg)"
                    }
                    transition={"transform 0.15s"}
                    color={"#8F8F8C"}
                    display={"flex"}
                  >
                    <SVG
                      src={"/icons-v1_1/chevron-double-left.svg"}
                      width={"14px"}
                      height={"14px"}
                    />
                  </Box>
                </Flex>
                {isOrderRulePanelOpen && (
                  <Box
                    padding={"8px 10px"}
                    maxH={"360px"}
                    overflowY={"auto"}
                  >
                    {matchedConditionList.length > 0 && (
                      <Box marginBottom={"8px"}>
                        <Text
                          fontSize={"10px"}
                          fontWeight={"700"}
                          color={"#7A6A00"}
                          marginBottom={"4px"}
                        >
                          {target.kind === "BRAND"
                            ? `[${target.name}] ${tTitle("matched_order_rule")}`
                            : tTitle("matched_order_rule")}
                        </Text>
                        <Flex flexWrap={"wrap"} gap={"4px"}>
                          {matchedConditionList.map((c, ci) => (
                            <Text
                              key={ci}
                              fontSize={"10px"}
                              color={"#7A6A00"}
                              background={"#FCFCFE"}
                              border={"0.5px solid #E5DCA0"}
                              borderRadius={"3px"}
                              padding={"1px 6px"}
                              whiteSpace={"nowrap"}
                            >
                              {formatMatchedConditionLabel(c)}
                            </Text>
                          ))}
                        </Flex>
                      </Box>
                    )}
                    {orderRuleFileList.length > 0 && (
                      <Flex
                        flexDirection={"column"}
                        gap={"4px"}
                        marginBottom={"8px"}
                      >
                        {orderRuleFileList.map((file) => {
                          const fileType =
                            file.name
                              .toLowerCase()
                              .split(".")
                              .at(1)
                              ?.toLowerCase() ?? "default";
                          return (
                            <Flex
                              key={file.id}
                              alignItems={"center"}
                              gap={"6px"}
                              padding={"6px"}
                              borderRadius={"4px"}
                              border={"0.5px solid #D4D4D1"}
                              background={"#FCFCFE"}
                            >
                              <Box flexShrink={0}>
                                <SVG
                                  src={
                                    fileIconsByType[fileType] ??
                                    fileIconsByType.default
                                  }
                                  width={"16px"}
                                  height={"16px"}
                                />
                              </Box>
                              <Text
                                fontSize={"11px"}
                                color={"#444444"}
                                flexGrow={1}
                                overflow={"hidden"}
                                textOverflow={"ellipsis"}
                                whiteSpace={"nowrap"}
                              >
                                {file.name}
                              </Text>
                              <Box
                                flexShrink={0}
                                cursor={"pointer"}
                                onClick={() => {
                                  fireDownloadUnoFile(
                                    { unoFileId: file.id },
                                    {}
                                  );
                                }}
                              >
                                <SVG
                                  src={"/icons-v1_1/download.svg"}
                                  width={"14px"}
                                  height={"14px"}
                                />
                              </Box>
                            </Flex>
                          );
                        })}
                      </Flex>
                    )}
                    {(orderRulesText?.trim() ?? "") !== "" ? (
                      <Text
                        fontSize={"11px"}
                        whiteSpace={"pre-wrap"}
                        lineHeight={1.6}
                        color={"#444444"}
                      >
                        {orderRulesText}
                      </Text>
                    ) : (
                      orderRuleFileList.length === 0 && (
                        <Text fontSize={"11px"} color={"#8F8F8C"}>
                          -
                        </Text>
                      )
                    )}
                  </Box>
                )}
              </Box>
            )}
            <Box flex={1} minW={0}>
              <ProductConditionPanel
                lineSheetSetId={lineSheetSetId}
                target={
                  target.kind === "SEASON"
                    ? {
                        kind: "SEASON",
                        id: target.id,
                        seasonName: target.seasonName,
                      }
                    : target.kind === "LINE_SHEET_BRAND"
                    ? {
                        kind: "LINE_SHEET_BRAND",
                        lineSheetId: target.lineSheetId,
                        brandId: target.brandId,
                      }
                    : target.kind === "SEASON_BRAND"
                    ? {
                        kind: "SEASON_BRAND",
                        seasonName: target.seasonName,
                        brandId: target.brandId,
                      }
                    : { kind: target.kind, id: target.id }
                }
                initialEntries={initialEntries}
                onEntriesChange={setEntries}
                orderRulesText={orderRulesText}
                orderRuleFiles={orderRuleFiles}
                matchedConditions={matchedConditions}
              />
            </Box>
          </Flex>
        </ModalBody>
        <ModalFooter display={"flex"} gap={"12px"}>
          <AppButton onClick={onClose}>{tTitle("not_now")}</AppButton>
          <AppButton variant={"primary"} onClick={handleConfirm}>
            {tTitle("apply")}
          </AppButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
